'use client';
import React, { useState } from 'react';
import { CalendarDays, Clock, MapPin, CreditCard, CheckCircle, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ReviewModal from '@/app/worker-profile-page/components/ReviewModal';

// Backend integration point: GET /api/employers/me/bookings
const allBookings = [
  {
    id: 'emp-booking-001',
    workerName: 'Thandi M.',
    workerId: 'worker-001',
    workerVetted: true,
    serviceType: 'Elder care — full day',
    date: '6 May 2026',
    time: '07:00–17:00',
    suburb: 'Waterkloof Ridge',
    status: 'confirmed',
    amount: 100,
    paidAt: '1 May 2026',
    reviewed: false,
  },
  {
    id: 'emp-booking-002',
    workerName: 'Lerato N.',
    workerId: 'worker-002',
    workerVetted: true,
    serviceType: 'Au pair — full day',
    date: '8 May 2026',
    time: '07:30–17:30',
    suburb: 'Faerie Glen',
    status: 'awaiting_payment',
    amount: 100,
    paidAt: null,
    reviewed: false,
  },
  {
    id: 'emp-booking-003',
    workerName: 'Sipho K.',
    workerId: 'worker-003',
    workerVetted: true,
    serviceType: 'House sitting — overnight',
    date: '12 May 2026',
    time: '18:00–08:00',
    suburb: 'Centurion',
    status: 'accepted',
    amount: 100,
    paidAt: null,
    reviewed: false,
  },
  {
    id: 'emp-booking-004',
    workerName: 'Nomvula D.',
    workerId: 'worker-004',
    workerVetted: true,
    serviceType: 'Elder care — half day',
    date: '28 Apr 2026',
    time: '08:00–13:00',
    suburb: 'Moreleta Park',
    status: 'completed',
    amount: 100,
    paidAt: '24 Apr 2026',
    reviewed: false,
  },
  {
    id: 'emp-booking-005',
    workerName: 'Ruan V.',
    workerId: 'worker-005',
    workerVetted: true,
    serviceType: 'Homework help',
    date: '25 Apr 2026',
    time: '14:00–17:00',
    suburb: 'Silver Lakes',
    status: 'completed',
    amount: 100,
    paidAt: '22 Apr 2026',
    reviewed: false,
  },
];

const statusConfig: Record<string, { label: string; bg: string; color: string }> = {
  requested: { label: 'Requested', bg: '#EEF2FF', color: '#4F46E5' },
  accepted: { label: 'Accepted — confirm to pay', bg: 'var(--success-bg)', color: 'var(--success)' },
  awaiting_payment: { label: 'Awaiting payment', bg: 'var(--warning-bg)', color: 'var(--warning)' },
  confirmed: { label: 'Confirmed', bg: 'var(--orange-pale)', color: 'var(--primary)' },
  completed: { label: 'Completed', bg: 'var(--muted)', color: 'var(--muted-foreground)' },
};

const tabs: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'accepted', label: 'Action needed' },
  { key: 'awaiting_payment', label: 'Pay now' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
];

interface ReviewTarget {
  bookingId: string;
  workerId: string;
  workerName: string;
}

export default function EmployerBookingPipeline() {
  const [activeTab, setActiveTab] = useState('all');
  const [bookings, setBookings] = useState(allBookings);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);

  const filtered = activeTab === 'all' ? bookings : bookings.filter((b) => b.status === activeTab);

  // Backend integration point: POST /api/bookings/:id/confirm-payment
  const handlePay = async (id: string) => {
    setPayingId(id);
    await new Promise((r) => setTimeout(r, 1600));
    setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status: 'confirmed', paidAt: '3 May 2026' } : b));
    setPayingId(null);
    toast.success('Payment confirmed — R100 booking fee paid', {
      description: 'Your booking is now confirmed. Address will be revealed 3 hours before start time.',
    });
  };

  const handleReviewSubmitted = (bookingId: string) => {
    setBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, reviewed: true } : b));
    toast.success('Review submitted — thank you for your feedback!');
  };

  return (
    <>
      <div className="card overflow-hidden">
        <div className="px-5 pt-5 pb-0">
          <h3 className="font-bold mb-4" style={{ color: 'var(--navy)' }}>My bookings</h3>

          {/* Pipeline tabs */}
          <div className="flex gap-0 border-b border-border -mx-5 px-5 overflow-x-auto">
            {tabs.map((tab) => {
              const count = tab.key === 'all' ? bookings.length : bookings.filter((b) => b.status === tab.key).length;
              return (
                <button
                  key={`emp-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className="px-3 py-2.5 text-xs font-semibold whitespace-nowrap transition-all duration-150 border-b-2 flex items-center gap-1.5"
                  style={{
                    borderColor: activeTab === tab.key ? 'var(--primary)' : 'transparent',
                    color: activeTab === tab.key ? 'var(--primary)' : 'var(--muted-foreground)',
                  }}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full font-bold tabular-nums"
                      style={{
                        background: activeTab === tab.key ? 'var(--primary)' : 'var(--border)',
                        color: activeTab === tab.key ? 'white' : 'var(--muted-foreground)',
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="p-8 text-center">
              <CalendarDays size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>No bookings here</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Browse candidates to request a booking.</p>
            </div>
          ) : (
            filtered.map((booking) => {
              const sc = statusConfig[booking.status] ?? statusConfig.requested;
              const isPayNow = booking.status === 'awaiting_payment' || booking.status === 'accepted';
              const isPaying = payingId === booking.id;

              return (
                <div key={booking.id} className="p-5 hover:bg-muted/20 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <span className="font-bold text-sm" style={{ color: 'var(--navy)' }}>{booking.serviceType}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        with <strong>{booking.workerName}</strong> {booking.workerVetted && <span style={{ color: 'var(--vetted-color)' }}>✓ Vetted</span>}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                    <span className="flex items-center gap-1"><CalendarDays size={11} />{booking.date}</span>
                    <span className="flex items-center gap-1"><Clock size={11} />{booking.time}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} />{booking.suburb}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      <CreditCard size={12} />
                      {booking.paidAt ? `R${booking.amount} paid · ${booking.paidAt}` : `R${booking.amount} booking fee`}
                    </div>

                    {isPayNow && (
                      <button
                        onClick={() => handlePay(booking.id)}
                        disabled={isPaying}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95 disabled:opacity-60"
                        style={{ background: 'var(--primary)', color: 'white', minWidth: '120px' }}
                      >
                        {isPaying ? (
                          <><Loader2 size={12} className="animate-spin" />Processing...</>
                        ) : (
                          <><CheckCircle size={12} />Confirm & Pay R100</>
                        )}
                      </button>
                    )}

                    {booking.status === 'completed' && (
                      booking.reviewed ? (
                        <span className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
                          <CheckCircle size={12} />
                          Reviewed
                        </span>
                      ) : (
                        <button
                          onClick={() => setReviewTarget({ bookingId: booking.id, workerId: booking.workerId, workerName: booking.workerName })}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95"
                          style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}
                        >
                          <Star size={12} />
                          Leave review
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {reviewTarget && (
        <ReviewModal
          workerName={reviewTarget.workerName}
          workerId={reviewTarget.workerId}
          bookingRef={reviewTarget.bookingId}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => {
            handleReviewSubmitted(reviewTarget.bookingId);
            setReviewTarget(null);
          }}
        />
      )}
    </>
  );
}